import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * ConfettiExplosion - Lucky Number Design System
 *
 * Celebratory confetti animation for winner moments and entry success.
 * Uses CSS animations for performance.
 */

const COLORS = [
  '#14B8A6', // primary (teal)
  '#2DD4BF', // primary-light
  '#F59E0B', // accent (amber)
  '#FBBF24', // accent-light
  '#10B981', // success
  '#FAF7F2', // cream
];

const SHAPES = ['square', 'circle', 'triangle'];

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function ConfettiPiece({ delay, duration, left, color, shape, size }) {
  const shapeStyles = {
    square: {},
    circle: { borderRadius: '50%' },
    triangle: {
      width: 0,
      height: 0,
      backgroundColor: 'transparent',
      borderLeft: `${size / 2}px solid transparent`,
      borderRight: `${size / 2}px solid transparent`,
      borderBottom: `${size}px solid ${color}`,
    },
  };

  return (
    <div
      className="absolute animate-confetti-fall"
      style={{
        left: `${left}%`,
        top: '-20px',
        width: shape === 'triangle' ? 0 : size,
        height: shape === 'triangle' ? 0 : size,
        backgroundColor: shape === 'triangle' ? 'transparent' : color,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        transform: `rotate(${randomBetween(0, 360)}deg)`,
        ...shapeStyles[shape],
      }}
    />
  );
}

export default function ConfettiExplosion({
  count = 50,
  duration = 3,
  className,
}) {
  const [pieces, setPieces] = useState([]);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Generate confetti pieces
    const newPieces = Array.from({ length: count }, (_, i) => ({
      id: i,
      left: randomBetween(0, 100),
      delay: randomBetween(0, 0.5),
      duration: randomBetween(duration * 0.8, duration * 1.2),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
      size: randomBetween(6, 12),
    }));
    setPieces(newPieces);

    // Hide confetti after animation
    const timeout = setTimeout(() => {
      setIsVisible(false);
    }, (duration + 1) * 1000);

    return () => clearTimeout(timeout);
  }, [count, duration]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 pointer-events-none overflow-hidden z-50',
        className
      )}
    >
      {pieces.map((piece) => (
        <ConfettiPiece key={piece.id} {...piece} />
      ))}
    </div>
  );
}

/**
 * useConfetti hook
 *
 * Trigger confetti programmatically.
 */
export function useConfetti() {
  const [showConfetti, setShowConfetti] = useState(false);

  const triggerConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);
  };

  return { showConfetti, triggerConfetti };
}
